import { useState } from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from 'react-i18next';
import { ClipboardCheck, ScanLine, ShieldAlert, ArrowRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useUIStore } from '@/stores/uiStore';
import { cn } from '@/lib/utils';

interface Step {
  icon: typeof ClipboardCheck;
  titleKey: string;
  bodyKey: string;
}

const STEPS: Step[] = [
  { icon: ClipboardCheck, titleKey: 'tour.accept.title', bodyKey: 'tour.accept.body' },
  { icon: ShieldAlert, titleKey: 'tour.cancel.title', bodyKey: 'tour.cancel.body' },
  { icon: ScanLine, titleKey: 'tour.pickup.title', bodyKey: 'tour.pickup.body' },
];

export function OnboardingTour() {
  const { t } = useTranslation();
  const { tutorialDismissed, dismissTutorial } = useUIStore();
  const [step, setStep] = useState(0);

  if (tutorialDismissed) return null;
  if (typeof document === 'undefined') return null;

  const current = STEPS[step];
  const Icon = current.icon;
  const isLast = step === STEPS.length - 1;

  const next = () => {
    if (isLast) dismissTutorial();
    else setStep(step + 1);
  };

  return createPortal(
    <div className="fixed inset-0 z-[80] flex items-end justify-center bg-foreground/40 p-4 backdrop-blur-sm sm:items-center">
      <div className="w-full max-w-md animate-fade-up rounded-lg border border-border bg-card p-6 shadow-ticket-hover">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-primary/10 text-primary">
              <Icon className="h-5 w-5" strokeWidth={2.2} />
            </div>
            <div className="label-tracked">{t('tour.heading', { current: step + 1, total: STEPS.length })}</div>
          </div>
          <button
            type="button"
            onClick={dismissTutorial}
            className="text-xs font-medium text-muted-foreground hover:text-foreground"
          >
            {t('tour.skip')}
          </button>
        </div>

        <h3 className="mt-4 font-mono text-2xl text-foreground">{t(current.titleKey)}</h3>
        <p className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{t(current.bodyKey)}</p>

        <div className="mt-5 flex items-center justify-between gap-3">
          <div className="flex gap-1.5">
            {STEPS.map((_, i) => (
              <span
                key={i}
                className={cn(
                  'h-1.5 rounded-full transition-all',
                  i === step ? 'w-6 bg-primary' : 'w-1.5 bg-muted',
                )}
              />
            ))}
          </div>
          <Button onClick={next} size="lg" className="gap-2">
            {isLast ? t('tour.done') : t('tour.next')}
            {!isLast && <ArrowRight className="h-4 w-4" />}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
